import React, { useState, useEffect } from 'react';
import { Home, DollarSign, TrendingUp, Calculator, ChevronDown, ChevronUp, Check, X } from 'lucide-react';

const RentVsBuyCalculator = ({ onGetQuote }) => {
  // Basic inputs
  const [homePrice, setHomePrice] = useState(500000);
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTerm, setLoanTerm] = useState(30);
  const [monthlyRent, setMonthlyRent] = useState(2500);

  // Advanced inputs (collapsed by default)
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [propertyTaxRate, setPropertyTaxRate] = useState(1.1);
  const [insurancePerYear, setInsurancePerYear] = useState(1500);
  const [hoaPerMonth, setHoaPerMonth] = useState(0);
  const [homeAppreciation, setHomeAppreciation] = useState(3);
  const [rentIncrease, setRentIncrease] = useState(3);
  const [maintenancePercent, setMaintenancePercent] = useState(1);

  // Comparison period
  const [years, setYears] = useState(10);

  // Results
  const [results, setResults] = useState(null);

  // Calculate mortgage payment
  const calculateMortgage = () => {
    const principal = homePrice * (1 - downPaymentPercent / 100);
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;

    if (monthlyRate === 0) {
      return principal / numPayments;
    }

    const monthlyPayment = principal *
      (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
      (Math.pow(1 + monthlyRate, numPayments) - 1);

    return monthlyPayment;
  };

  // Calculate all results
  useEffect(() => {
    const downPayment = homePrice * (downPaymentPercent / 100);
    const loanAmount = homePrice - downPayment;
    const monthlyMortgage = calculateMortgage();
    const monthlyPropertyTax = (homePrice * propertyTaxRate / 100) / 12;
    const monthlyInsurance = insurancePerYear / 12;
    const monthlyMaintenance = (homePrice * maintenancePercent / 100) / 12;

    const totalMonthlyBuyCost = monthlyMortgage + monthlyPropertyTax + monthlyInsurance + hoaPerMonth + monthlyMaintenance;

    // Calculate over the comparison period
    let totalRentPaid = 0;
    let totalBuyCost = downPayment; // Start with down payment
    let currentRent = monthlyRent;
    let currentHomeValue = homePrice;
    let remainingLoan = loanAmount;
    const monthlyRate = interestRate / 100 / 12;

    for (let year = 1; year <= years; year++) {
      // Rent costs for the year
      for (let month = 0; month < 12; month++) {
        totalRentPaid += currentRent;
      }
      currentRent *= (1 + rentIncrease / 100);

      // Buy costs for the year
      const yearlyPropertyTax = currentHomeValue * propertyTaxRate / 100;
      const yearlyMaintenance = currentHomeValue * maintenancePercent / 100;

      for (let month = 0; month < 12; month++) {
        // Monthly mortgage payment breakdown
        const interestPayment = remainingLoan * monthlyRate;
        const principalPayment = monthlyMortgage - interestPayment;
        remainingLoan = Math.max(0, remainingLoan - principalPayment);

        totalBuyCost += monthlyMortgage + yearlyPropertyTax / 12 + monthlyInsurance + hoaPerMonth + yearlyMaintenance / 12;
      }

      // Home appreciation
      currentHomeValue *= (1 + homeAppreciation / 100);
    }

    // Calculate equity and net position
    const homeEquity = currentHomeValue - remainingLoan;

    // Calculate when buying becomes better (break-even)
    let breakEvenYear = null;
    let tempRent = monthlyRent;
    let tempHomeValue = homePrice;
    let tempRemainingLoan = loanAmount;
    let tempTotalRent = 0;
    let tempTotalBuyCost = downPayment;

    for (let year = 1; year <= 30; year++) {
      for (let month = 0; month < 12; month++) {
        tempTotalRent += tempRent;

        const interestPayment = tempRemainingLoan * monthlyRate;
        const principalPayment = monthlyMortgage - interestPayment;
        tempRemainingLoan = Math.max(0, tempRemainingLoan - principalPayment);

        const yearlyPropertyTax = tempHomeValue * propertyTaxRate / 100;
        const yearlyMaintenance = tempHomeValue * maintenancePercent / 100;
        tempTotalBuyCost += monthlyMortgage + yearlyPropertyTax / 12 + insurancePerYear / 12 + hoaPerMonth + yearlyMaintenance / 12;
      }

      tempRent *= (1 + rentIncrease / 100);
      tempHomeValue *= (1 + homeAppreciation / 100);

      const tempEquity = tempHomeValue - tempRemainingLoan;
      const tempNetBuy = tempEquity - tempTotalBuyCost + downPayment;
      const tempNetRent = -tempTotalRent;

      if (tempNetBuy > tempNetRent && !breakEvenYear) {
        breakEvenYear = year;
        break;
      }
    }

    setResults({
      monthlyMortgage: Math.round(monthlyMortgage),
      totalMonthlyBuyCost: Math.round(totalMonthlyBuyCost),
      monthlyRent,
      totalRentPaid: Math.round(totalRentPaid),
      totalBuyCost: Math.round(totalBuyCost),
      homeEquity: Math.round(homeEquity),
      futureHomeValue: Math.round(currentHomeValue),
      remainingLoan: Math.round(remainingLoan),
      difference: Math.round(homeEquity - (totalBuyCost - totalRentPaid)),
      breakEvenYear,
      isBuyingBetter: homeEquity > (totalBuyCost - totalRentPaid),
      downPayment: Math.round(downPayment),
      loanAmount: Math.round(loanAmount)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homePrice, downPaymentPercent, interestRate, loanTerm, monthlyRent, propertyTaxRate, insurancePerYear, hoaPerMonth, homeAppreciation, rentIncrease, maintenancePercent, years]);

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-[#0d1424] to-[#0a0f1c]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10b981]/20 border border-[#10b981]/40 mb-6">
            <Calculator className="w-5 h-5 text-[#10b981]" />
            <span className="text-[#10b981] font-semibold">Smart Calculator</span>
          </div>
          <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4">
            Should You <span className="text-[#10b981]">Rent or Buy</span>?
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Compare the true cost of renting vs. buying over {years} years. With a good mortgage rate, buying is often the smarter choice!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Panel */}
          <div className="bg-[#141b2d] rounded-3xl p-6 md:p-8 border border-gray-800">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Home className="w-5 h-5 text-[#10b981]" />
              Basic Information
            </h3>

            {/* Home Price */}
            <div className="mb-5">
              <label className="block text-gray-300 font-medium mb-2">
                Home Price
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="text"
                  value={formatNumber(homePrice)}
                  onChange={(e) => setHomePrice(Number(e.target.value.replace(/,/g, '')) || 0)}
                  className="w-full pl-8 pr-4 py-3 bg-[#0a0f1c] border border-gray-700 rounded-xl text-white focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                />
              </div>
            </div>

            {/* Down Payment */}
            <div className="mb-5">
              <label className="block text-gray-300 font-medium mb-2">
                Down Payment: {downPaymentPercent}% ({formatCurrency(homePrice * downPaymentPercent / 100)})
              </label>
              <input
                type="range"
                min="0"
                max="50"
                value={downPaymentPercent}
                onChange={(e) => setDownPaymentPercent(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#10b981]"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
              </div>
            </div>

            {/* Interest Rate */}
            <div className="mb-5">
              <label className="block text-gray-300 font-medium mb-2">
                Interest Rate: {interestRate}%
              </label>
              <input
                type="range"
                min="3"
                max="10"
                step="0.125"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#10b981]"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>3%</span>
                <span>6.5%</span>
                <span>10%</span>
              </div>
            </div>

            {/* Loan Term */}
            <div className="mb-5">
              <label className="block text-gray-300 font-medium mb-2">
                Loan Term
              </label>
              <div className="flex gap-2">
                {[15, 20, 30].map((term) => (
                  <button
                    key={term}
                    onClick={() => setLoanTerm(term)}
                    className={`flex-1 py-3 rounded-xl font-semibold transition-all ${
                      loanTerm === term
                        ? 'bg-[#10b981] text-white'
                        : 'bg-[#0a0f1c] text-gray-400 border border-gray-700 hover:border-[#10b981]/50'
                    }`}
                  >
                    {term} years
                  </button>
                ))}
              </div>
            </div>

            {/* Monthly Rent */}
            <div className="mb-5">
              <label className="block text-gray-300 font-medium mb-2">
                Monthly Rent
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="text"
                  value={formatNumber(monthlyRent)}
                  onChange={(e) => setMonthlyRent(Number(e.target.value.replace(/,/g, '')) || 0)}
                  className="w-full pl-8 pr-4 py-3 bg-[#0a0f1c] border border-gray-700 rounded-xl text-white focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                />
              </div>
            </div>

            {/* Comparison Period */}
            <div className="mb-5">
              <label className="block text-gray-300 font-medium mb-2">
                Compare Over: {years} years
              </label>
              <input
                type="range"
                min="1"
                max="30"
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#10b981]"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1 year</span>
                <span>15 years</span>
                <span>30 years</span>
              </div>
            </div>

            {/* Advanced Options Toggle */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-[#10b981] font-medium mb-4 hover:text-[#059669] transition-colors"
            >
              {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Advanced Options
            </button>

            {/* Advanced Options */}
            {showAdvanced && (
              <div className="space-y-4 pt-4 border-t border-gray-700">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Property Tax (%/yr)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={propertyTaxRate}
                      onChange={(e) => setPropertyTaxRate(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-[#0a0f1c] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#10b981]"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Insurance ($/yr)</label>
                    <input
                      type="number"
                      value={insurancePerYear}
                      onChange={(e) => setInsurancePerYear(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-[#0a0f1c] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#10b981]"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">HOA ($/mo)</label>
                    <input
                      type="number"
                      value={hoaPerMonth}
                      onChange={(e) => setHoaPerMonth(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-[#0a0f1c] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#10b981]"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Maintenance (%/yr)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={maintenancePercent}
                      onChange={(e) => setMaintenancePercent(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-[#0a0f1c] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#10b981]"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Home Appreciation (%/yr)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={homeAppreciation}
                      onChange={(e) => setHomeAppreciation(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-[#0a0f1c] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#10b981]"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Rent Increase (%/yr)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={rentIncrease}
                      onChange={(e) => setRentIncrease(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-[#0a0f1c] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#10b981]"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Results Panel */}
          <div className="space-y-6">
            {results && (
              <>
                {/* Main Result Card */}
                <div className={`rounded-3xl p-6 md:p-8 border-2 ${
                  results.isBuyingBetter
                    ? 'bg-gradient-to-br from-[#10b981]/20 to-[#059669]/10 border-[#10b981]'
                    : 'bg-gradient-to-br from-orange-500/20 to-orange-600/10 border-orange-500'
                }`}>
                  <div className="text-center mb-6">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 ${
                      results.isBuyingBetter ? 'bg-[#10b981]/30' : 'bg-orange-500/30'
                    }`}>
                      {results.isBuyingBetter ? (
                        <Check className="w-5 h-5 text-[#10b981]" />
                      ) : (
                        <X className="w-5 h-5 text-orange-500" />
                      )}
                      <span className={`font-bold ${results.isBuyingBetter ? 'text-[#10b981]' : 'text-orange-500'}`}>
                        {results.isBuyingBetter ? 'Buying is Better!' : 'Renting is Better'}
                      </span>
                    </div>

                    <h3 className="text-white text-lg mb-2">After {years} years, you'll have:</h3>
                    <div className="text-4xl md:text-5xl font-bold text-[#10b981] mb-2">
                      {formatCurrency(results.homeEquity)}
                    </div>
                    <p className="text-gray-400">in Home Equity</p>
                  </div>

                  {results.breakEvenYear && (
                    <div className="text-center p-4 bg-[#0a0f1c]/50 rounded-xl">
                      <p className="text-gray-300">
                        <TrendingUp className="w-4 h-4 inline mr-1 text-[#10b981]" />
                        Buying becomes better starting <span className="text-[#10b981] font-bold">year {results.breakEvenYear}</span>
                      </p>
                    </div>
                  )}
                </div>

                {/* Comparison Cards */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Buy Card */}
                  <div className="bg-[#141b2d] rounded-2xl p-5 border border-[#10b981]/30">
                    <div className="flex items-center gap-2 mb-4">
                      <Home className="w-5 h-5 text-[#10b981]" />
                      <h4 className="text-white font-bold">Buying</h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-gray-500 text-sm">Monthly Payment</p>
                        <p className="text-white font-semibold">{formatCurrency(results.monthlyMortgage)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Total Monthly Cost</p>
                        <p className="text-white font-semibold">{formatCurrency(results.totalMonthlyBuyCost)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Home Value in {years} Years</p>
                        <p className="text-[#10b981] font-semibold">{formatCurrency(results.futureHomeValue)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Remaining Loan</p>
                        <p className="text-white font-semibold">{formatCurrency(results.remainingLoan)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Rent Card */}
                  <div className="bg-[#141b2d] rounded-2xl p-5 border border-gray-700">
                    <div className="flex items-center gap-2 mb-4">
                      <DollarSign className="w-5 h-5 text-gray-400" />
                      <h4 className="text-white font-bold">Renting</h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-gray-500 text-sm">Monthly Rent</p>
                        <p className="text-white font-semibold">{formatCurrency(results.monthlyRent)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Total Rent ({years} Years)</p>
                        <p className="text-orange-400 font-semibold">{formatCurrency(results.totalRentPaid)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Asset Value</p>
                        <p className="text-gray-400 font-semibold">$0</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Money Lost</p>
                        <p className="text-red-400 font-semibold">-{formatCurrency(results.totalRentPaid)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Insight */}
                <div className="bg-[#141b2d] rounded-2xl p-6 border border-gray-800">
                  <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#10b981]" />
                    Key Takeaways
                  </h4>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#10b981] flex-shrink-0 mt-0.5" />
                      <span>
                        Rent payments of <span className="text-orange-400 font-semibold">{formatCurrency(results.totalRentPaid)}</span> over {years} years are gone forever
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#10b981] flex-shrink-0 mt-0.5" />
                      <span>
                        Buying: You'll own an asset worth <span className="text-[#10b981] font-semibold">{formatCurrency(results.homeEquity)}</span> after {years} years
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#10b981] flex-shrink-0 mt-0.5" />
                      <span>
                        At <span className="text-[#10b981] font-semibold">{interestRate}%</span> interest, every payment builds YOUR wealth, not your landlord's
                      </span>
                    </li>
                  </ul>
                </div>

                {/* CTA */}
                <button
                  onClick={onGetQuote}
                  className="w-full py-4 bg-[#10b981] hover:bg-[#059669] text-white font-bold text-lg rounded-2xl transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-[#10b981]/30"
                >
                  Get Your Best Rate Quote
                </button>
                <p className="text-center text-gray-500 text-sm">
                  Free - No obligation - Results in minutes
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RentVsBuyCalculator;
